import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, Trophy, Copy, ArrowRight, BookOpen } from "lucide-react";
import { toast } from "sonner";

const Members = () => {
  const { memberToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/members/${memberToken}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "Member link not found. Check your welcome email." : "Could not load library."))
      .finally(() => setLoading(false));
  }, [memberToken]);

  if (loading) {
    return <div className="grain min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-[#8b0000]" size={32} />
    </div>;
  }
  if (err) {
    return <div className="grain min-h-screen flex items-center justify-center">
      <div className="card-noir p-8 max-w-md text-center">
        <div className="font-display text-xl text-[#f4f0ec] mb-2">{err}</div>
        <Link to="/founder" className="btn-ghost text-sm">Back to Founder page</Link>
      </div>
    </div>;
  }

  const f = data.founder;
  return (
    <div className="grain min-h-screen" data-testid="members-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-8">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="mb-10">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-1">your private library</div>
          <h1 className="font-display text-5xl text-[#f4f0ec] mb-2">
            Welcome back, <span translate="no">{f.name || "Founder"}</span>.
          </h1>
          <div className="flex items-center gap-3 text-sm text-[#a39797] flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#d4af37] text-[#d4af37]">
              <Trophy size={12} /> Founder #{f.founder_number || "?"}
            </span>
            <span>Member since <span translate="no">{(f.created_at || "").slice(0, 10)}</span></span>
          </div>
        </div>

        {/* Coupon */}
        <div className="card-noir p-5 mb-10 border-[#d4af37]" data-testid="founder-coupon-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs tracking-widest text-[#a39797]">YOUR 25% OFF CODE</div>
              <div className="font-mono text-3xl text-[#d4af37] mt-1 tracking-[0.2em]" translate="no" data-testid="founder-coupon-code">
                {f.coupon_code}
              </div>
              <div className="text-xs text-[#a39797] mt-2">Use at checkout on any kit, pack, or Night upgrade. Never expires.</div>
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(f.coupon_code); toast.success("Code copied."); }}
              className="btn-ghost text-sm"
              data-testid="founder-copy-code"
            >
              <Copy size={14} /> Copy
            </button>
          </div>
        </div>

        {/* Case list */}
        <div className="mb-4 flex items-center gap-2">
          <BookOpen size={14} className="text-[#d4af37]" />
          <div className="text-xs tracking-widest text-[#a39797]">EVERY CASE — UNLOCKED</div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {data.cases.map((c) => (
            <Link
              key={c.id}
              to={`/cases/${c.slug || c.id}`}
              className="card-noir p-4 hover:border-[#d4af37] transition-colors flex items-start justify-between gap-2"
              data-testid={`member-case-${c.id}`}
            >
              <div className="min-w-0">
                <div className="text-[10px] tracking-widest text-[#5a5050]" translate="no">#{c.number || "—"}</div>
                <div className="font-display text-lg text-[#f4f0ec] truncate" translate="no">{c.title}</div>
                {c.subtitle && <div className="text-xs text-[#a39797] mt-1 truncate">{c.subtitle}</div>}
              </div>
              <ArrowRight size={14} className="text-[#d4af37] mt-1 shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Members;
